// Zod contracts for the identity module. Owned by M2 (C2); nothing else edits this file.
// Every request and response schema lives here (architecture/architecture.md Section 3.11).
import { z } from 'zod';
import { type Channel, CODE_LISTS, type PrincipalKind } from '../constants/code-lists.js';
import {
  CLIENT_NAME_MAX,
  isValidAccess,
  MCP_CLIENT_IDENTIFICATIONS,
  MCP_CLIENT_KINDS,
  MCP_CLIENT_PURPOSE_MAX,
  publishedIdentityUrlProblem,
  REDIRECT_URIS_MAX,
} from '../constants/mcp-clients.js';
import { APP_ROLE_CODES, PERMISSION_CODES, TOOL_ROLE_CODES } from '../constants/roles.js';

// ---------------------------------------------------------------------------------------------
// The actor every call carries (architecture Section 6, "Actors")
// ---------------------------------------------------------------------------------------------

export interface Actor {
  principalId: string;
  /**
   * person | platform_admins | system | triage_llm | mcp_app (an app client acting as itself through MCP, poc CT-014;
   * ai_coding_agent is legacy: old history only, poc CT-007)
   */
  kind: PrincipalKind;
  /** Equal to principalId for people. */
  personId?: string;
  /**
   * Set when an MCP client acts for the person ("Claude on behalf of Alex Chen"), and on an app client's own actor
   * (kind mcp_app, poc CT-014), whose history reads "<client> (app)" with no "on behalf of".
   */
  mcpClientId?: string;
  channel: Channel;
  displayName: string;
}

export const ChannelSchema = z.enum(CODE_LISTS.channel);
export const PrincipalKindSchema = z.enum(CODE_LISTS.principal_kind);
export const IdentitySourceSchema = z.enum(CODE_LISTS.identity_source);
export const SecurityGroupPurposeSchema = z.enum(CODE_LISTS.security_group_purpose);
export const GroupSyncPathSchema = z.enum(CODE_LISTS.group_sync_path);
export const GroupSyncOutcomeSchema = z.enum(CODE_LISTS.group_sync_outcome);
export const AiUseSchema = z.enum(CODE_LISTS.ai_use);
export const PermissionCodeSchema = z.enum(PERMISSION_CODES);
export const ToolRoleCodeSchema = z.enum(TOOL_ROLE_CODES);
export const AppRoleCodeSchema = z.enum(APP_ROLE_CODES);

const Uuid = z.uuid();
const IsoDateTime = z.iso.datetime({ offset: true });
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const EntraObjectIdSchema = z.string().regex(GUID, 'Must be an Entra object ID (a GUID)');

/** A principal shown in pickers and lists. */
export const PrincipalSummarySchema = z.object({
  principalId: Uuid,
  kind: PrincipalKindSchema,
  displayName: z.string(),
  /** People only. */
  upn: z.string().nullable(),
  /**
   * people.email (PII: never sent to the LLM, never logged); null when Entra has none, and for anything but a
   * person. M14 finds an assignee's Linear account by it (module-2 Section 2.2, decision D1).
   */
  email: z.string().nullable(),
});
export type PrincipalSummary = z.infer<typeof PrincipalSummarySchema>;

// ---------------------------------------------------------------------------------------------
// Session API (GET /api/session)
// ---------------------------------------------------------------------------------------------

/** Staff see every app with role `owner`, shown as "Editor" (poc CT-008). */
export const SessionAppSchema = z.object({
  appId: Uuid,
  name: z.string(),
  role: AppRoleCodeSchema,
});

export const SessionViewSchema = z.object({
  person: z.object({ principalId: Uuid, displayName: z.string(), upn: z.string() }),
  csrfToken: z.string(),
  isPlatformAdmin: z.boolean(),
  /** True when the person is in the Editors or the Admins group (staff pages; poc CT-008). */
  isStaff: z.boolean(),
  toolRoles: z.array(ToolRoleCodeSchema),
  apps: z.array(SessionAppSchema),
  permissions: z.object({
    tool: z.array(PermissionCodeSchema),
    byApp: z.record(z.string(), z.array(PermissionCodeSchema)),
  }),
  isTestSignIn: z.boolean(),
  signedInAt: IsoDateTime,
  expiresAt: IsoDateTime,
});
export type SessionView = z.infer<typeof SessionViewSchema>;

// ---------------------------------------------------------------------------------------------
// Sign-in (architecture 7.1, 7.3, 7.5)
// ---------------------------------------------------------------------------------------------

/** A relative path on the tool: starts with "/" but not "//" or "/\". */
export const ReturnToSchema = z
  .string()
  .max(2000)
  .refine((v) => v.startsWith('/') && !v.startsWith('//') && !v.startsWith('/\\'), 'Must be a relative path');

export const SignInQuerySchema = z.object({ returnTo: z.string().max(2000).optional() });

export const CallbackQuerySchema = z.object({
  code: z.string().max(4000).optional(),
  state: z.string().max(500).optional(),
  error: z.string().max(200).optional(),
  error_description: z.string().max(2000).optional(),
});

export const TestLoginBodySchema = z.object({
  /** An Entra object ID or a principal ID. */
  person: z.string().min(1).max(200),
});

export const WidgetSignInQuerySchema = z.object({
  origin: z.string().max(500),
  nonce: z.string().min(8).max(200),
});

/** postMessage from the widget sign-in pop-up to window.opener (architecture 7.3). */
export const WidgetTokenMessageSchema = z.object({
  type: z.literal('hpg-feedback:token'),
  nonce: z.string(),
  token: z.string(),
  expiresAt: IsoDateTime,
  displayName: z.string(),
});
export type WidgetTokenMessage = z.infer<typeof WidgetTokenMessageSchema>;

export const ReportingTokenSchema = z.object({
  token: z.string(),
  expiresAt: IsoDateTime,
  displayName: z.string(),
});
export type ReportingToken = z.infer<typeof ReportingTokenSchema>;

/** The reporting token's audience (architecture 7.3). */
export const REPORTING_TOKEN_AUDIENCE = 'feedback-widget';
export const REPORTING_TOKEN_SCOPE = 'report';
/** The Entra delegated scope the SDK's tokens carry (REQ-14.2). */
export const FEEDBACK_REPORT_SCOPE = 'Feedback.Report';

// ---------------------------------------------------------------------------------------------
// Security groups (architecture 7.2)
// ---------------------------------------------------------------------------------------------

export const GroupSyncRunSchema = z.object({
  id: Uuid,
  securityGroupId: Uuid,
  groupDisplayName: z.string(),
  path: GroupSyncPathSchema,
  startedAt: IsoDateTime,
  finishedAt: IsoDateTime.nullable(),
  outcome: GroupSyncOutcomeSchema.nullable(),
  detail: z.string().nullable(),
});
export type GroupSyncRun = z.infer<typeof GroupSyncRunSchema>;

export const SecurityGroupViewSchema = z.object({
  id: Uuid,
  entraGroupId: z.string(),
  displayName: z.string(),
  purpose: SecurityGroupPurposeSchema,
  memberCount: z.number().int().min(0),
  /** Demo groups (ID not a GUID) are never sent to Graph. */
  isDemo: z.boolean(),
  addedAt: IsoDateTime,
  lastSyncedAt: IsoDateTime.nullable(),
  lastSync: GroupSyncRunSchema.nullable(),
});
export type SecurityGroupView = z.infer<typeof SecurityGroupViewSchema>;

/**
 * GET /api/admin/security-groups (poc CT-008): the tool's two groups, Admins (purpose platform_admin) and
 * Editors. Either is null while it isn't set.
 */
export const SecurityGroupsViewSchema = z.object({
  admins: SecurityGroupViewSchema.nullable(),
  editors: SecurityGroupViewSchema.nullable(),
});
export type SecurityGroupsView = z.infer<typeof SecurityGroupsViewSchema>;

/** POST: adds the Editors group while none is set (409 otherwise). Admins is set only by the bootstrap seed. */
export const AddSecurityGroupBodySchema = z.object({
  entraGroupId: EntraObjectIdSchema,
  purpose: z.literal('editors'),
});

/** PUT /api/admin/security-groups/editors: points the Editors group at another Entra group. */
export const ReplaceEditorsGroupBodySchema = z.object({
  entraGroupId: EntraObjectIdSchema,
});

/** The answer to adding or replacing the Editors group. */
export const AddSecurityGroupResponseSchema = z.object({
  group: SecurityGroupViewSchema,
});

export const SyncSummarySchema = z.object({
  path: GroupSyncPathSchema,
  groups: z.array(
    z.object({
      securityGroupId: Uuid,
      displayName: z.string(),
      outcome: GroupSyncOutcomeSchema,
      members: z.number().int().min(0),
      detail: z.string().nullable(),
    }),
  ),
  skippedDemoGroups: z.number().int().min(0),
  leaversHandled: z.number().int().min(0),
});
export type SyncSummary = z.infer<typeof SyncSummarySchema>;

export const SyncRunsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

// ---------------------------------------------------------------------------------------------
// AI platforms and MCP clients registers (REQ-13.1, REQ-19.5; CT-022, CT-024)
// ---------------------------------------------------------------------------------------------

/** The providers of the LLMs deployed in an environment's Microsoft Foundry resource (LLM_MODELS, CT-022). */
export const FOUNDRY_PROVIDERS = ['openai', 'anthropic'] as const;
export const FoundryProviderSchema = z.enum(FOUNDRY_PROVIDERS);
export type FoundryProvider = z.infer<typeof FoundryProviderSchema>;

/**
 * One entry of Admin > AI platforms: an LLM deployed in this environment's Microsoft Foundry resource, used for AI tasks
 * (triage). CT-024: every entry is one (no AI tools, no approval record, no Active switch: an LLM is always configured
 * with what it needs to work). `foundry.configured` says whether LLM_MODELS lists it; the page shows only those.
 */
export const AiPlatformViewSchema = z.object({
  id: Uuid,
  name: z.string(),
  uses: z.array(AiUseSchema),
  foundry: z.object({
    provider: FoundryProviderSchema,
    deployment: z.string(),
    configured: z.boolean(),
  }),
  recordedAt: IsoDateTime,
});
export type AiPlatformView = z.infer<typeof AiPlatformViewSchema>;

/**
 * PATCH: the LLM's uses only (CT-024). ['triage'] moves triage here from the LLM that had it (only to an LLM LLM_MODELS
 * lists); dropping triage from the LLM that holds it is a 400: triage always runs on one LLM.
 */
export const UpdateAiPlatformBodySchema = z.object({
  uses: z.array(AiUseSchema).max(CODE_LISTS.ai_use.length),
});
export type UpdateAiPlatformBody = z.infer<typeof UpdateAiPlatformBodySchema>;

/**
 * Microsoft's abuse-monitoring data storage on this environment's Foundry resource, read live from Azure (CT-022):
 * 'off' only when the resource's capabilities hold ContentLogging=false (modified abuse monitoring approved).
 * 'unknown' with a reason when FOUNDRY_RESOURCE_ID isn't set or the read failed.
 */
export const FoundryStatusSchema = z.object({
  storage: z.enum(['on', 'off', 'unknown']),
  checkedAt: IsoDateTime,
  resourceName: z.string().nullable(),
  location: z.string().nullable(),
  subscriptionId: z.string().nullable(),
  /**
   * Each deployment's settings as Azure reports them (CT-023, standard AIP-13): its deployment type (where prompts are
   * processed: Standard, DataZoneStandard, GlobalStandard...) and its content-filter (Guardrails) policy. Empty when
   * they couldn't be read.
   */
  deployments: z.array(
    z.object({ name: z.string(), deploymentType: z.string().nullable(), contentFilter: z.string().nullable() }),
  ),
  reason: z.enum(['not_configured', 'check_failed']).optional(),
});
export type FoundryStatus = z.infer<typeof FoundryStatusSchema>;
/** `?refresh=1` reads Azure again instead of the 5-minute cached answer ("Check again"). */
export const FoundryStatusQuerySchema = z.object({ refresh: z.literal('1').optional() });

/** https, or http on a loopback host (RFC 8252: any port on loopback). */
export const RedirectUriSchema = z
  .url()
  .max(1000)
  .refine((v) => {
    const url = new URL(v);
    const loopback = ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
    return url.protocol === 'https:' || (url.protocol === 'http:' && loopback);
  }, 'Redirect URIs must be https, or http on a loopback address')
  .refine((v) => !new URL(v).hash, 'Redirect URIs can’t have a fragment');

export const McpClientKindSchema = z.enum(MCP_CLIENT_KINDS);
/** How a people client identifies itself (poc CT-015): a tool-issued client ID, or a published identity's URL. */
export const McpClientIdentificationSchema = z.enum(MCP_CLIENT_IDENTIFICATIONS);

/** A published identity's URL (poc CT-015): https, with a path, no fragment, user name or password. */
export const PublishedIdentityUrlSchema = z
  .string()
  .trim()
  .superRefine((value, ctx) => {
    const problem = publishedIdentityUrlProblem(value);
    if (problem) {
      ctx.addIssue({ code: 'custom', message: problem });
    }
  });

/** What a client can do (poc CT-012): Read is always on; Modify and Delete need Write. */
export const McpClientAccessSchema = z
  .object({ write: z.boolean(), modify: z.boolean(), delete: z.boolean() })
  .refine(isValidAccess, 'Modify and Delete need Write');

/** An app client's Entra application ID (a GUID). */
const EntraApplicationIdSchema = z.string().regex(GUID, 'Must be an Entra application ID (a GUID)');
const McpClientPurposeSchema = z
  .string()
  .trim()
  .min(1, 'Say what the app uses the tool for')
  .max(MCP_CLIENT_PURPOSE_MAX);

/** A published identity's state (poc CT-015), on a people client registered with one. */
export const PublishedIdentityViewSchema = z.object({
  url: z.string(),
  documentName: z.string().nullable(),
  /** The last good copy's fetch time; null until it's first fetched. */
  fetchedAt: IsoDateTime.nullable(),
  /** fetchedAt + 7 days: until then the last good copy is used when a refetch fails. */
  usableUntil: IsoDateTime.nullable(),
  /** The last fetch failed (and when, and why); null when it succeeded. */
  lastFetchFailed: z.object({ at: IsoDateTime, reason: z.string() }).nullable(),
});
export type PublishedIdentityView = z.infer<typeof PublishedIdentityViewSchema>;

/**
 * An MCP client (poc CT-012). `people`: identified by `oauthClientId`, with its redirect URIs (a client registered
 * before CT-012 may also have an `entraClientId`). `app`: identified by `entraClientId`, no redirect URIs, with an
 * owner and a purpose (null on an app client registered before CT-012). No client has a secret.
 * poc CT-014: an app client calls as itself; `lastUsedAt` is its last accepted call (to the minute, null: never),
 * and `ownerHasLeft` is true when its owner is no longer an active Editor or Admin (computed on read; the app keeps
 * working until an Admin chooses a new owner). Both are null/false on a people client.
 * poc CT-015: `identifiedBy` `published` (a people client only): `oauthClientId` is its published identity's URL,
 * `redirectUris` are its document's (from the last good copy; none until it's first fetched), and
 * `publishedIdentity` says when it was last fetched, until when that copy keeps working if a refetch fails, and
 * whether the last fetch failed. `documentName` is the document's own client_name: the tool shows people the
 * registered `displayName`, never it.
 */
export const McpClientViewSchema = z.object({
  id: Uuid,
  displayName: z.string(),
  kind: McpClientKindSchema,
  identifiedBy: McpClientIdentificationSchema,
  oauthClientId: z.string(),
  entraClientId: z.string().nullable(),
  isAllowed: z.boolean(),
  access: McpClientAccessSchema,
  owner: z.object({ id: Uuid, displayName: z.string() }).nullable(),
  purpose: z.string().nullable(),
  ownerHasLeft: z.boolean(),
  lastUsedAt: IsoDateTime.nullable(),
  redirectUris: z.array(z.string()),
  publishedIdentity: PublishedIdentityViewSchema.nullable(),
  registeredAt: IsoDateTime,
});
export type McpClientView = z.infer<typeof McpClientViewSchema>;

const McpClientFields = {
  displayName: z.string().trim().min(1).max(CLIENT_NAME_MAX),
  isAllowed: z.boolean(),
};
const RedirectUrisSchema = z.array(RedirectUriSchema).max(REDIRECT_URIS_MAX);

const PeopleClientFields = {
  kind: z.literal('people'),
  ...McpClientFields,
  isAllowed: McpClientFields.isAllowed.default(false),
};

/**
 * Register a client: how it connects comes first, and decides the rest. It starts with Read only. A people client
 * identifies itself with a tool-issued client ID (the default; its redirect URIs listed here) or, poc CT-015, a
 * published identity (its URL; its redirect URIs come from its document, which the service fetches and checks).
 */
export const CreateMcpClientBodySchema = z.discriminatedUnion('kind', [
  z.discriminatedUnion('identifiedBy', [
    z.object({
      ...PeopleClientFields,
      identifiedBy: z.literal('client_id').default('client_id'),
      redirectUris: RedirectUrisSchema.min(1, 'Add at least one redirect URI'),
    }),
    z.object({
      ...PeopleClientFields,
      identifiedBy: z.literal('published'),
      publishedIdentityUrl: PublishedIdentityUrlSchema,
    }),
  ]),
  z.object({
    kind: z.literal('app'),
    ...McpClientFields,
    isAllowed: McpClientFields.isAllowed.default(false),
    entraClientId: EntraApplicationIdSchema,
    ownerPersonId: Uuid,
    purpose: McpClientPurposeSchema,
  }),
]);
export type CreateMcpClientBody = z.infer<typeof CreateMcpClientBodySchema>;

/**
 * PATCH: only the fields sent change (no defaults). Which ones a client takes depends on its kind, which can't
 * change (the service answers a field error): redirect URIs for `people`; the Entra application ID, owner and
 * purpose for `app` (owner and purpose can't be cleared). `access` is the whole set of switches.
 * poc CT-015: how a people client identifies itself, and a published identity's URL, can't change either (a field
 * error unless they're the client's own); a published identity's redirect URIs come from its document, never here.
 */
export const UpdateMcpClientBodySchema = z
  .object({
    ...McpClientFields,
    kind: McpClientKindSchema,
    identifiedBy: McpClientIdentificationSchema,
    publishedIdentityUrl: PublishedIdentityUrlSchema,
    redirectUris: RedirectUrisSchema,
    entraClientId: EntraApplicationIdSchema.nullable(),
    ownerPersonId: Uuid,
    purpose: McpClientPurposeSchema,
    access: McpClientAccessSchema,
  })
  .partial();
export type UpdateMcpClientBody = z.infer<typeof UpdateMcpClientBodySchema>;

/**
 * POST /api/admin/mcp-clients/published-identity/preview (poc CT-015; tool.manage_settings, CSRF): the tool fetches
 * and checks the document at the URL an Admin typed, before they register it. A document it can't use is a 400
 * field error on `publishedIdentityUrl` with the reason.
 */
export const PublishedIdentityPreviewBodySchema = z.object({ publishedIdentityUrl: PublishedIdentityUrlSchema });
export type PublishedIdentityPreviewBody = z.infer<typeof PublishedIdentityPreviewBodySchema>;

export const PublishedIdentityPreviewSchema = z.object({
  url: z.string(),
  /** The document's client_name, to prefill the registered name (people only ever see the registered name). */
  clientName: z.string().nullable(),
  /** The redirect URIs the tool will accept: https on the URL's own host, or loopback (any port). */
  redirectUris: z.array(z.string()),
  /** Listed in the document but never accepted (another host, or http that isn't loopback). */
  ignoredRedirectUris: z.array(z.string()),
  /** The client already registered with this URL, if any: it can't be registered twice. */
  alreadyRegisteredAs: z.string().nullable(),
});
export type PublishedIdentityPreview = z.infer<typeof PublishedIdentityPreviewSchema>;

// ---------------------------------------------------------------------------------------------
// People lookups (REQ-5.2, REQ-8.5)
// ---------------------------------------------------------------------------------------------

export const PEOPLE_SEARCH_LIMIT = 20;

/**
 * Active members of the Editors and Admins groups whose name or UPN matches `q` (poc CT-008): one tool-wide
 * set for every picker. Other query parameters are ignored.
 */
export const PeopleQuerySchema = z.object({
  q: z.string().trim().max(200).default(''),
});

export const IdParamSchema = z.object({ id: Uuid });
export const AppIdParamSchema = z.object({ appId: Uuid });
